import { useParams, useSearchParams, Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueGuideBody } from "@/components/VenueGuideBody";
import { CTAButton } from "@/components/CTAButton";
import { onImgError } from "@/lib/images";
import { Calendar } from "lucide-react";

export default function VenueGuidePage() {
  const { slug, venue: venueParam, league, team } = useParams();
  const venueSlug = venueParam || slug;
  const { data, loading, error } = useFetch(`/venue-guide/${venueSlug}`, [venueSlug]);

  if (loading) return <Loading />;
  if (error || !data?.venue) return <DetailError error={error} path={`/venues/${venueSlug}`} notFound={!error} />;
  const v = data.venue;

  const crumbs = league && team
    ? [{ label: "GameDay", to: "/gameday" }, { label: league.toUpperCase(), to: `/${league}` }, { label: team.replace(/-/g, " "), to: `/${league}/${team}` }, { label: v.name }]
    : [{ label: "GameDay", to: "/gameday" }, { label: "Venues" }, { label: v.name }];

  const jsonLd = {
    "@context": "https://schema.org", "@type": "StadiumOrArena", name: v.name,
    address: { "@type": "PostalAddress", streetAddress: v.address }, maximumAttendeeCapacity: v.capacity,
    geo: v.lat ? { "@type": "GeoCoordinates", latitude: v.lat, longitude: v.lng } : undefined,
  };

  return (
    <>
      <Seo title={v.seo_title || `${v.name} — Where to Stay | Hotelstrips`} description={v.seo_description || v.description}
        image={v.image_url} path={`/venues/${v.slug}`} type="article" jsonLd={jsonLd} />

      <section className="relative min-h-[60vh] flex items-end">
        <img src={v.image_url} alt={v.image_alt || v.name} onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 img-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full pb-12">
          <div className="overline text-primary mb-3">{v.city_slug?.replace(/-/g, " ")} • Venue guide</div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter text-white max-w-3xl leading-[0.98]">{v.name}</h1>
          {data.teams?.length > 0 && (
            <p className="text-white/70 mt-3">Home of {data.teams.map((t) => t.name).join(" & ")}</p>
          )}
          <div className="mt-6" data-testid="venue-hero-cta">
            <CTAButton
              productType="hotels"
              size="lg"
              label={`Find hotels near ${v.name}`}
              testId="venue-hero-hotels-cta"
              ctx={{ venue: v.slug, city: v.city_slug, page: `venue_${v.slug}`, position: "hero" }}
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={crumbs} />
        <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mb-12">{v.description}</p>

        {data.events?.length > 0 && (
          <div className="mb-12 flex flex-wrap gap-3">
            {data.events.map((e) => (
              <Link key={e.slug} to={`/events/${e.slug}`} className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-card px-4 py-2 text-sm font-semibold text-ink hover:border-primary hover:text-primary transition-colors">
                <Calendar className="h-4 w-4" /> {e.title}
              </Link>
            ))}
          </div>
        )}

        <VenueGuideBody bundle={data} ctx={{}} pageName={`venue_${v.slug}`} />
      </div>
    </>
  );
}
